import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { describeError } from '../api/client';
import { useServices } from '../app-context';
import { ErrorBanner, ExposureBadge, StatusBadge } from '../components/badges';
import { useAsync } from '../hooks/use-async';
import { EnrollmentSection } from './tenant/EnrollmentSection';
import { HealthSection } from './tenant/HealthSection';
import { MiddlewaresSection } from './tenant/MiddlewaresSection';
import { OpsSection } from './tenant/OpsSection';
import { SettingsSection } from './tenant/SettingsSection';
import { VersionSection } from './tenant/VersionSection';
import { WelcomeEmailSection } from './tenant/WelcomeEmailSection';

export function TenantDetailPage() {
  const { code = '' } = useParams();
  const { api } = useServices();
  const navigate = useNavigate();
  const tenant = useAsync(() => api.getTenant(code), [api, code]);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    try {
      await api.deleteTenant(code);
      void navigate('/');
    } catch (e) {
      setError(describeError(e));
    }
  };

  if (tenant.error) return <ErrorBanner message={tenant.error} />;
  if (!tenant.data) return <p>Loading…</p>;
  const t = tenant.data;

  return (
    <div>
      <p>
        <Link to="/">← Tenants</Link>
      </p>
      <h1>
        {t.name} <small>({t.code})</small> <StatusBadge status={t.status} />{' '}
        <ExposureBadge mode={t.exposureMode} />
      </h1>
      <p>
        Canonical: <strong>{t.canonicalHostname}</strong> · Tailscale:{' '}
        {t.tailscaleAddress ?? 'not set'}
      </p>
      <ErrorBanner message={error} />
      <HealthSection code={t.code} />
      <VersionSection tenant={t} />
      <EnrollmentSection
        code={t.code}
        token={t.enrollmentToken}
        enrolledAt={t.enrolledAt}
        bootstrapAdminEmail={t.bootstrapAdminEmail}
        onChanged={tenant.reload}
      />
      {!t.enrolledAt && (
        <WelcomeEmailSection code={t.code} status={t.welcomeEmail} onChanged={tenant.reload} />
      )}
      <SettingsSection tenant={t} onSaved={tenant.reload} />
      <MiddlewaresSection code={t.code} />
      <OpsSection code={t.code} enabled={Boolean(t.enrolledAt && t.tailscaleAddress)} />
      {t.status === 'disabled' && (
        <section aria-label="Danger zone">
          <h2>Danger zone</h2>
          <button type="button" className="danger" onClick={() => void remove()}>
            Delete tenant
          </button>
        </section>
      )}
    </div>
  );
}
